import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC
} from 'react';
import { useAui, useAuiState } from '@assistant-ui/react';
import { parseFollowups, type McpFollowup } from '../utils/followupMarkers';
import {
  backgroundAnswerText,
  useBackgroundReports
} from './skill-flow/backgroundReportStore';
import { McpUserApiKeyWarningAlert } from './McpUserApiKeyWarningAlert';
import { McpInsufficientWalletAlert } from './McpInsufficientWalletAlert';
import { McpChatErrorAlert } from './McpChatErrorAlert';

const EMPTY_FOLLOWUPS: McpFollowup[] = [];

const SKILL_PDF_FOLLOWUP: McpFollowup = {
  label: 'خروجی PDF این گزارش رو بهم بده',
  send: 'خروجی PDF این گزارش رو بهم بده'
};

const MAX_FOLLOWUPS = 3;

function extractTextFromMessage(message: {
  content: unknown;
}): string {
  if (!Array.isArray(message.content)) {
    return typeof message.content === 'string' ? message.content : '';
  }

  const parts: string[] = [];
  for (const part of message.content) {
    if (
      part &&
      typeof part === 'object' &&
      'type' in part &&
      part.type === 'text' &&
      'text' in part
    ) {
      parts.push(String((part as { text: string }).text));
    }
  }
  return parts.join('\n');
}

function isPdfFollowup(item: McpFollowup): boolean {
  const blob = `${item.label}\n${item.send}`.toLowerCase();
  return (
    blob.includes('pdf') ||
    blob.includes('پی دی اف') ||
    blob.includes('پی‌دی‌اف') ||
    blob.includes('خروجی pdf')
  );
}

/** Skill report turn: overview UI and/or skill api_result tool payload. */
function threadHasSkillReport(messages: readonly { content: unknown }[]): boolean {
  for (const message of messages) {
    if (!Array.isArray(message.content)) continue;
    for (const part of message.content) {
      if (!part || typeof part !== 'object' || !('type' in part)) continue;
      const row = part as {
        type?: string;
        toolName?: string;
        result?: unknown;
        argsText?: string;
      };
      if (row.type !== 'tool-call') continue;
      if (row.toolName === 'present_skill_overview') return true;
      if (
        typeof row.toolName === 'string' &&
        row.toolName.startsWith('configure_')
      ) {
        const resultText =
          typeof row.result === 'string'
            ? row.result
            : row.result != null
              ? JSON.stringify(row.result)
              : '';
        if (
          resultText.includes('"phase": "api_result"') ||
          resultText.includes('"phase":"api_result"')
        ) {
          return true;
        }
      }
    }
  }
  return false;
}

function ensureSkillPdfFollowup(
  followups: McpFollowup[],
  skillReport: boolean
): McpFollowup[] {
  if (!skillReport) return followups;
  if (followups.some(isPdfFollowup)) {
    return followups.slice(0, MAX_FOLLOWUPS);
  }
  return [SKILL_PDF_FOLLOWUP, ...followups].slice(0, MAX_FOLLOWUPS);
}

function appendPrompt(
  aui: ReturnType<typeof useAui>,
  prompt: string
): void {
  const trimmed = prompt.trim();
  if (!trimmed) return;
  try {
    const thread =
      typeof (aui as { thread?: unknown }).thread === 'function'
        ? (
            aui as {
              thread: () => { append: (message: string) => void };
            }
          ).thread()
        : (
            aui as {
              thread: { append: (message: string) => void };
            }
          ).thread;
    thread.append(trimmed);
  } catch (error) {
    console.error('Failed to send follow-up suggestion', error);
  }
}

const McpFollowupChips: FC<{ followups: McpFollowup[] }> = ({ followups }) => {
  const aui = useAui();
  const scrollRef = useRef<HTMLDivElement>(null);
  const rtlRef = useRef<boolean | null>(null);
  const [fades, setFades] = useState({ left: false, right: false });

  const updateFades = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const fromStart = Math.abs(el.scrollLeft);
    const rtl = (rtlRef.current ??= getComputedStyle(el).direction === 'rtl');
    const [left, right] = rtl
      ? [maxScroll - fromStart, fromStart]
      : [fromStart, maxScroll - fromStart];
    setFades(prev => {
      const next = { left: left > 1, right: right > 1 };
      return prev.left === next.left && prev.right === next.right ? prev : next;
    });
  }, []);

  useEffect(() => {
    updateFades();
    const el = scrollRef.current;
    if (!el?.firstElementChild) return undefined;
    const observer = new ResizeObserver(updateFades);
    observer.observe(el);
    observer.observe(el.firstElementChild);
    return () => observer.disconnect();
  }, [updateFades, followups]);

  const maskImage = `linear-gradient(to right, ${
    fades.left ? 'transparent, black 2rem' : 'black'
  }, ${fades.right ? 'black calc(100% - 2rem), transparent' : 'black'})`;

  return (
    <div
      ref={scrollRef}
      onScroll={updateFades}
      className="aui-thread-followup-suggestions mt-3 w-full overflow-x-auto py-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      style={{ maskImage, WebkitMaskImage: maskImage }}
      dir="rtl"
      aria-label="سوالات پیشنهادی"
    >
      <div className="flex w-full flex-wrap items-center justify-start gap-2 px-0.5">
        {followups.map((item, idx) => (
          <button
            key={`${item.label}-${idx}`}
            type="button"
            className="aui-thread-followup-suggestion bg-background hover:bg-muted/80 text-foreground rounded-full border px-3 py-1.5 text-sm leading-snug whitespace-normal transition-colors ease-in text-start"
            onClick={() => appendPrompt(aui, item.send)}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * Renders inside an assistant message: user-API-key fallback warnings, then
 * FOLLOWUPS chips under the latest assistant turn.
 *
 * useAuiState compares with Object.is — selectors must return stable
 * primitives/refs, never a fresh array/object each call.
 */
export const McpMessageFollowups: FC = () => {
  const isLatestAssistant = useAuiState(s => {
    if (s.thread.isRunning) return false;
    const messages = s.thread.messages;
    const last = messages[messages.length - 1];
    return (
      last?.role === 'assistant' &&
      last.id === s.message.id &&
      s.message.role === 'assistant'
    );
  });

  const sourceText = useAuiState(s => {
    if (s.message.role !== 'assistant') return '';
    return extractTextFromMessage(s.message);
  });
  const backgroundReports = useBackgroundReports();
  const reportText = backgroundAnswerText(
    useAuiState(s =>
      s.message.role === 'assistant' ? s.message.content : null
    ),
    backgroundReports
  );

  const skillReport = useAuiState(s =>
    threadHasSkillReport(s.thread.messages)
  );

  const followups = useMemo(() => {
    if (!isLatestAssistant) return EMPTY_FOLLOWUPS;
    const combined = [sourceText, reportText].filter(Boolean).join('\n');
    const parsed = combined ? parseFollowups(combined) : EMPTY_FOLLOWUPS;
    const merged = ensureSkillPdfFollowup(parsed, skillReport);
    return merged.length > 0 ? merged : EMPTY_FOLLOWUPS;
  }, [isLatestAssistant, reportText, skillReport, sourceText]);

  return (
    <>
      <McpUserApiKeyWarningAlert />
      <McpInsufficientWalletAlert />
      <McpChatErrorAlert />
      {followups.length > 0 ? (
        <McpFollowupChips followups={followups} />
      ) : null}
    </>
  );
};

/** No composer-level followups for MCP — chips live under the assistant reply. */
export const McpFollowupSuggestions: FC = () => null;
